import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  ArrowLeft, 
  Heart, 
  Sparkles, 
  Star, 
  Crown, 
  Key, 
  Gift,
  ChevronDown,
  ChevronUp,
  Mail,
  User
} from 'lucide-react';
import UserMenu from '@/components/UserMenu';
import Footer from '@/components/Footer';
import VIPModal from '@/components/VIPModal';
import { useAuth } from '@/context/AuthContext';

// NUMENAXIS Symbol Component (replaces Ankh)
const NumenaxisSymbol = ({ className = "w-6 h-6" }) => (
  <img 
    src="/images/numenaxis-symbol-transparent.png"
    alt="NUMENAXIS"
    className={`${className} object-contain`}
    draggable={false}
  />
);

export default function NumenaxisNaujienosPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showStory, setShowStory] = useState(false);
  const [showVIPModal, setShowVIPModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setShowVIPModal(true);
  };

  return (
    <div className="min-h-screen pb-16">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-foreground/70 hover:text-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Grįžti
          </Button>
          <UserMenu />
        </div>

        {/* Page Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <NumenaxisSymbol className="w-10 h-10" />
            <h1 className="font-display text-3xl sm:text-4xl bg-gradient-to-r from-amber-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
              Numenaxis Naujienos
            </h1>
            <NumenaxisSymbol className="w-10 h-10" />
          </div>
          <p className="text-muted-foreground italic">
            Kelionė į savęs pažinimą prasideda nuo supratimo
          </p>
        </div>

        {/* Author Introduction Card */}
        <Card className="bg-gradient-to-br from-purple-950/50 via-indigo-950/40 to-slate-950/50 border-amber-500/20 p-6 sm:p-8 mb-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Author Photo Placeholder */}
            <div className="flex-shrink-0">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-amber-500/30 to-purple-500/30 border-2 border-amber-500/40 flex items-center justify-center">
                <User className="w-16 h-16 text-amber-400/60" />
              </div>
            </div>
            
            {/* Author Welcome */}
            <div className="text-center md:text-left">
              <h2 className="font-display text-xl text-amber-400 mb-3">
                Sveika, Numenaxis siela... ✨
              </h2>
              <p className="text-foreground/80 leading-relaxed">
                Prieš tau žengiant giliau, noriu, kad žinotum – tu nesi tik vartotojas. 
                Tu esi dalis bendruomenės, kurią kuriu su didžiausia meile ir atsakomybe. 
                Numenaxis gimė iš noro sukurti erdvę, kurioje nebūtų triukšmo, reklamų ar skubėjimo. 
                Tai <span className="font-bold text-white">tyli sala</span> tavo kasdienybėje, 
                kurioje gali susitikti su savo tikrąja esybe.
              </p>
            </div>
          </div>
        </Card>

        {/* Why Membership Section */}
        <Card className="card-mystical p-6 sm:p-8 mb-6 border-primary/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Heart className="w-5 h-5 text-amber-400" />
            </div>
            <h3 className="font-display text-xl text-amber-400">Kodėl verta rinktis narystę?</h3>
          </div>
          
          <div className="text-foreground/80 leading-relaxed space-y-4">
            <p>
              Mes tikime, kad savęs pažinimo kelias reikalauja <span className="font-bold text-white">tylos ir ramumo</span>, 
              todėl sąmoningai atsisakėme reklamų, kurios blaškytų tavo dėmesį.
            </p>
            <p>
              Tavo pasirinkta narystė leidžia mums išlaikyti šią erdvę švarią ir nepriklausomą, 
              o tau – atrakina duris į pačias giliausias „Numenaxis" analizes, 
              kurios taps tavo <span className="font-bold text-white">kasdieniais sielos raktais</span>.
            </p>
          </div>
        </Card>

        {/* Membership Plans Section */}
        <Card className="bg-gradient-to-br from-slate-950/80 to-purple-950/40 border-amber-500/30 p-6 sm:p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-amber-400" />
            </div>
            <h3 className="font-display text-xl text-amber-400">Mūsų bendra kelionė (Narystės planai)</h3>
          </div>

          {/* Simplified Plan Cards - Clean & Peaceful */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Monthly VIP */}
            <div className="bg-slate-900/50 border border-amber-500/30 rounded-xl p-6 text-center hover:border-amber-500/50 transition-all duration-300">
              <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <Key className="w-7 h-7 text-amber-400" />
              </div>
              <h4 className="font-display text-xl text-amber-400 mb-1">🗝️ Mėnesinis VIP</h4>
              <p className="text-2xl font-bold text-foreground mb-2">€2.99</p>
              <p className="text-sm text-amber-300/70 italic mb-5">Tavo kasdienis palydovas</p>
              <Button
                onClick={() => handlePlanSelect('monthly')}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold"
              >
                Pasirinkti
              </Button>
            </div>

            {/* Yearly VIP */}
            <div className="bg-gradient-to-br from-purple-950/60 to-indigo-950/60 border-2 border-purple-500/40 rounded-xl p-6 text-center hover:border-purple-500/60 transition-all duration-300 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                POPULIARIAUSIAS
              </div>
              <div className="w-14 h-14 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4 mt-2">
                <Star className="w-7 h-7 text-purple-400" />
              </div>
              <h4 className="font-display text-xl text-purple-400 mb-1">🌟 Metinis VIP</h4>
              <p className="text-2xl font-bold text-foreground mb-2">€25</p>
              <p className="text-sm text-purple-300/70 italic mb-5">Gilusis žemėlapis</p>
              <Button
                onClick={() => handlePlanSelect('yearly')}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold"
              >
                Pasirinkti
              </Button>
            </div>

            {/* Premium VIP */}
            <div className="bg-gradient-to-br from-amber-950/50 to-yellow-950/30 border-2 border-amber-500/50 rounded-xl p-6 text-center hover:border-amber-400/70 transition-all duration-300 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                RIBOTAS
              </div>
              <div className="w-14 h-14 rounded-full bg-amber-500/30 flex items-center justify-center mx-auto mb-4 mt-2">
                <Crown className="w-7 h-7 text-amber-400" />
              </div>
              <h4 className="font-display text-xl text-amber-400 mb-1">💎 VIP+ Konsultacijos</h4>
              <p className="text-2xl font-bold text-foreground mb-2">€65</p>
              <p className="text-sm text-amber-300/70 italic mb-5">Artimiausias ryšys</p>
              <Button
                onClick={() => handlePlanSelect('premium')}
                className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-black font-bold"
              >
                Pasirinkti
              </Button>
            </div>
          </div>
        </Card>

        {/* Collapsible Story Section */}
        <Card className="card-mystical p-6 sm:p-8 mb-6 border-purple-500/20">
          <button
            onClick={() => setShowStory(!showStory)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Heart className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="font-display text-xl text-purple-400">Mano Išpažintis Tau</h3>
                <p className="text-sm text-muted-foreground">(Kodėl kaina tokia prieinama?)</p>
              </div>
            </div>
            {showStory ? (
              <ChevronUp className="w-6 h-6 text-purple-400" />
            ) : (
              <ChevronDown className="w-6 h-6 text-purple-400" />
            )}
          </button>

          {showStory && (
            <div className="mt-6 pt-6 border-t border-purple-500/20 text-foreground/80 leading-relaxed space-y-4">
              <p className="text-lg italic text-purple-300">
                Kodėl taip pigu?
              </p>
              <p>
                Šį klausimą man užduoda dažnai.
                Ir atsakymas nėra vien tik apie kainą.
              </p>
              <p>
                Maždaug prieš pusmetį, regresinės sesijos metu, patyriau vieną labai stiprų vaizdinį. 
                Nesvarbu, kaip jį pavadinsime — reinkarnacija, pasąmonės simboliu ar gilia atminties metafora. 
                Svarbu tai, ką jis manyje atvėrė.
              </p>
              <p>
                Toje patirtyje aš pamačiau save kitoje rolėje - kaip žmogų, kuris turėjo galimybę padėti, bet nepadėjo. 
                Žmogų, kuris vietoj atjautos pasirinko savanaudiškumą. 
                Toje istorijoje aš buvau vyras, miręs vienatvėje, su labai aiškiu suvokimu — 
                dėl to, kad moteris neturėjo pinigų susimokėti už dukros gydymą, aš uždariau duris į galimybę pasveikti.
              </p>
              <p className="font-bold text-white italic">
                Ir tada gimė priesaika. Ne garsiai ištarta, bet giliai įrašyta:
              </p>
              <p className="bg-purple-950/30 border-l-4 border-purple-500/50 pl-4 py-2 italic">
                Kitoje savo reinkarnacijoje, kad ir ką aš darysiu dėl žmonių, tai nebus tik išrinktiesiems. 
                Kad tai, kas ateina ne iš manęs, o PER mane, nebus sustabdyta mano savanaudiško godumo.
              </p>
              <p>
                Supratau, kad tik pats patyręs ribotumą ir tamsą, įgyji stebuklingą galią iš tikrųjų suprasti tai, 
                ką jaučia kitas žmogus.
              </p>
              <p>
                Būtent todėl man sunku priimti milžiniškas konsultacijų kainas.<br />
                Būtent todėl man keista, kai paprasta, universali informacija paverčiama prabanga.<br />
                Ir būtent todėl Numenaxis nėra apie „kiek galiu uždirbti", o apie tai, 
                <span className="font-bold text-white"> kaip galiu likti sąžininga sau, ir pasiekti daugiau širdžių</span>.
              </p>
              <p>
                Taip, aš esu žmogus.<br />
                Aš, kaip ir visi, noriu gražaus ir patogaus gyvenimo, bet žmonės neturi mokėti už mano norus.<br />
                Jie moka tik už tai, kas jiems yra naudinga, prasminga ir, žinoma, prieinama.<br />
                Visa kita — mano atsakomybė.
              </p>
              <p>
                Galima tuo tikėti kaip karma.<br />
                Galima skaityti kaip psichologinę tiesą.<br />
                Galima tiesiog jausti, kad kai kurie pasirinkimai kyla iš gilesnio sluoksnio nei verslo strategija.
              </p>
              <p className="font-bold text-white">
                Ir šis — vienas iš jų.<br />
                Tai mano atsakomybė, ir mano sąmoningas pasirinkimas.
              </p>
            </div>
          )}
        </Card>

        {/* Important Notice */}
        <Card className="bg-amber-500/10 border-amber-500/30 p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Crown className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h4 className="font-semibold text-amber-400 mb-2">Svarbu:</h4>
              <p className="text-foreground/80 leading-relaxed">
                Siekdama išlaikyti prasmingą asmeninį ryšį, <span className="font-bold text-white">VIP+ planų kiekį riboju</span>. 
                Noriu užtikrinti aukščiausią kokybę, kad mūsų bendras laikas virstų giliais pokyčiais – 
                tiek, kiek tau to reikia, ir tiek, kiek šiandien esi pasiruošęs (-usi) integruoti šiuos atradimus į savo kasdienybę.
              </p>
            </div>
          </div>
        </Card>

        {/* Contact Section */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-muted-foreground">
            <Mail className="w-4 h-4" />
            <span>Klausimai? </span>
            <a href="mailto:info@numenaxis.com" className="text-primary hover:text-primary-glow">
              info@numenaxis.com
            </a>
          </div>
        </div>
      </div>

      {/* VIP Modal */}
      <VIPModal
        isOpen={showVIPModal}
        onClose={() => setShowVIPModal(false)}
        onSuccess={() => setShowVIPModal(false)}
        userData={user}
        initialPlan={selectedPlan}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
}
